import type { ExpandableSearchHandle } from '@components/v2/ui/ExpandableSearch/expandableSearch.types';
import { useEffect, useRef } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useModalState } from '../../../../../../hooks/useModalState';
import { ClarityEvents } from '../../../../../../lib/clarity/types/clarityEvents.types';
import { captureEvent } from '../../../../../../lib/clarity/utils/captureEvent.utils';
import useOrderBookStore from '../../../../../../stores/OrderBook/orderBook.store';
import useIsAdminSeller from '../../../../../../stores/ShowUnavailableProducts/hooks/useIsAdminSeller';
import { SearchOption } from '../../../../../Search/interfaces/search-option.enum';
import type { OnSearchProduct } from '../../../../../Search/interfaces/use-search-product.interface';
import useSearchProduct from '../../../../../Search/useSearchProduct';
import type { ProductCodeSearchProps } from './productCodeSearch.types';

const useProductCodeSearch = ({ mode, verifyBrandContext, catalogId }: ProductCodeSearchProps) => {
  const isAdminSeller = useIsAdminSeller();
  const fieldRef = useRef<ExpandableSearchHandle>(null);
  const isAddMode = mode === 'add';
  const option = isAddMode ? SearchOption.ADD : SearchOption.SEARCH;

  const {
    isOpen: isOpenBarcodeModal,
    openModal: openBarcodeModal,
    closeModal: closeBarcodeModal,
  } = useModalState();

  const {
    status,
    isLoading,
    inputCode,
    errorMessage,
    selectedOption,
    onSearch,
    onAdd,
    onChangeInputCode,
    onChangeSelectedOption,
  } = useSearchProduct();

  const [selectedOrder, reloadOrderItemsQuantity] = useOrderBookStore(
    useShallow((state) => [state.selectedOrder, state.reloadOrderItemsQuantity]),
  );

  useEffect(() => {
    if (isAddMode && selectedOption === SearchOption.ADD) {
      fieldRef.current?.open();
    }
  }, [isAddMode, selectedOption]);

  const buildParams = (code: string): OnSearchProduct => ({
    option,
    code: code?.trim(),
    order: selectedOrder,
    reloadOrderItemsQuantity,
    isAdminSeller,
    catalogId,
  });

  const run = (code: string) => {
    if (isAddMode) {
      captureEvent(ClarityEvents.ORDER_SCREEN_PRODUCT_ADDED_BY_CODE_TOPBAR);
      return onAdd(buildParams(code));
    }
    captureEvent(ClarityEvents.SHELL_SEARCHED_PRODUCT_BY_CODE_TOPBAR);
    return onSearch(buildParams(code));
  };

  const withBrandContext = (callback: () => void) =>
    verifyBrandContext ? verifyBrandContext(callback) : callback();

  const onEnter = (code: string) => withBrandContext(() => run(code));

  const handleTriggerClick = (open: () => void) => {
    if (isAddMode) {
      captureEvent(ClarityEvents.ORDER_SCREEN_CLICKED_ADD_PRODUCT_BY_CODE_TOPBAR);
    }
    open();
  };

  const handleBarcodeClick = () => {
    captureEvent(
      isAddMode
        ? ClarityEvents.ORDER_SCREEN_CLICKED_BARCODE_READER_TOPBAR
        : ClarityEvents.SHELL_CLICKED_BARCODE_READER_TOPBAR,
    );
    openBarcodeModal();
  };

  const handleOpen = () => {
    if (isAddMode) {
      onChangeSelectedOption(SearchOption.ADD);
    }
  };

  const handleClose = () => {
    if (isAddMode) {
      onChangeSelectedOption(SearchOption.NONE);
    }
  };

  const onBarcodeSuccess = (decoded: string) => {
    closeBarcodeModal();
    withBrandContext(async () => {
      await run(decoded);
      if (!isAddMode) {
        fieldRef.current?.collapse();
      }
    });
  };

  return {
    fieldRef,
    value: inputCode,
    onChange: onChangeInputCode,
    onEnter,
    handleTriggerClick,
    handleOpen,
    handleClose,
    isLoading,
    status,
    errorMessage,
    isOpenBarcodeModal,
    handleBarcodeClick,
    closeBarcodeModal,
    onBarcodeSuccess,
  };
};

export default useProductCodeSearch;
